import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useSettings } from "../../lib/queries";
import { optimizeImg } from "../../lib/img";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="reveal-mask">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: [0.2, 0.9, 0.2, 1] }}
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

function Counter({ target, duration = 1600, suffix = "" }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const startTime = performance.now();
    const num = typeof target === "number" ? target : parseFloat(target) || 0;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(num * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return (
    <span className="font-display text-4xl md:text-5xl text-white">
      {v}
      {suffix}
    </span>
  );
}

export default function Hero() {
  const { t, lang, pick } = useLang();
  const { data: settings } = useSettings();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 180]);
  const opacity = useTransform(scrollY, [0, 600], [1, 0.2]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.06]);

  if (!settings) return <div className="min-h-screen bg-[#08080D]" />;

  const highlight = pick(settings, "hero_highlight");
  const title = pick(settings, "hero_title") || settings.hero_title_tr;
  const parts = title.split(highlight);

  const scrollToNext = () =>
    document.getElementById("marquee")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden grid-backdrop"
    >
      <div className="hero-radial absolute inset-0 pointer-events-none" />
      <div className="noise absolute inset-0" />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-16 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="w-8 h-px bg-[#A855F7]" />
              <span className="font-mono-ui text-[10px] tracking-[0.3em] text-[#A855F7] uppercase">
                {pick(settings, "hero_kicker") || settings.hero_kicker_tr}
              </span>
            </motion.div>

            <h1 className="font-display text-[13vw] leading-[0.88] uppercase tracking-tighter md:text-[7.2rem] lg:text-[8rem] text-white">
              <MaskedLine delay={0.15}>
                {lang === "tr" ? "Markanızı" : "We Make"}
              </MaskedLine>
              <br />
              <MaskedLine delay={0.28}>
                <span className="text-white/40 italic font-light">
                  {lang === "tr" ? "Viral Değil," : "Your Brand"}
                </span>
              </MaskedLine>
              <br />
              <MaskedLine delay={0.42}>
                <span className="text-[#A855F7]">
                  {highlight}
                </span>{" "}
                <span className="text-white">
                  {lang === "tr" ? "Yapıyoruz." : "Not Just Viral."}
                </span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-10 text-white/60 text-base md:text-lg max-w-xl leading-relaxed"
            >
              {pick(settings, "hero_description")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <button
                data-testid="hero-cta-projects"
                onClick={() =>
                  document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" })
                }
                className="btn-primary"
              >
                {t("cta_projects")}
                <ArrowUpRight size={16} />
              </button>
              <button
                data-testid="hero-cta-quote"
                onClick={() =>
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
                }
                className="btn-ghost"
              >
                {t("cta_quote")}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8"
            >
              {[
                { v: settings.stats_projects, s: "+", l: t("stats_projects") },
                { v: settings.stats_brands, s: "+", l: t("stats_brands") },
                { v: 0, l: t("stats_views"), custom: settings.stats_views },
                { v: settings.stats_years, s: "", l: t("stats_years") },
              ].map((it, i) => (
                <div key={i} data-testid={`hero-stat-${i}`}>
                  {it.custom ? (
                    <span className="font-display text-4xl md:text-5xl text-white">
                      {it.custom}
                    </span>
                  ) : (
                    <Counter target={it.v} suffix={it.s} />
                  )}
                  <div className="mt-2 text-[11px] uppercase tracking-widest text-white/50">
                    {it.l}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right — cinematic image with REC overlays */}
          <motion.div
            style={{ y, opacity, scale }}
            className="lg:col-span-5 relative"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.2, 0.9, 0.2, 1] }}
              className="relative aspect-[3/4] rounded-2xl overflow-hidden glow-purple"
            >
              <img
                src={optimizeImg(settings.hero_image, 900, 78)}
                alt="Ixirli production"
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080D] via-transparent to-[#08080D]/40" />

              {/* Top-left REC */}
              <div className="absolute top-4 left-4 flex items-center gap-2 font-mono-ui text-[10px] tracking-widest uppercase text-white">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                REC · 00:00:24:16
              </div>

              {/* Top-right badge */}
              <div className="absolute top-4 right-4 text-right font-mono-ui text-[9px] tracking-[0.25em] uppercase text-white/70">
                <div>Ixirli Production</div>
                <div className="text-[#A855F7]">Creative Agency</div>
              </div>

              {/* Bottom-left frame corners */}
              <div className="absolute inset-6 pointer-events-none border-l border-t border-white/40 w-8 h-8" />
              <div className="absolute right-6 top-6 pointer-events-none border-r border-t border-white/40 w-8 h-8" />
              <div className="absolute left-6 bottom-6 pointer-events-none border-l border-b border-white/40 w-8 h-8" />
              <div className="absolute right-6 bottom-6 pointer-events-none border-r border-b border-white/40 w-8 h-8" />

              {/* Bottom timecode */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono-ui text-[10px] tracking-widest uppercase text-white/80">
                <span>SCN · 01 / TAKE 04</span>
                <span className="text-[#A855F7]">4K · 24FPS</span>
              </div>
            </motion.div>

            {/* Floating card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="absolute -bottom-6 -left-6 hidden md:block card-glass p-4 w-56"
            >
              <div className="text-[10px] font-mono-ui tracking-widest text-[#A855F7] uppercase">
                Now Filming
              </div>
              <div className="mt-1 text-sm text-white font-medium">
                {lang === "tr" ? "Yeni Sezon — 2026" : "New Season — 2026"}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <button
        onClick={scrollToNext}
        data-testid="hero-scroll-indicator"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40 hover:text-white transition-colors z-10"
      >
        <span className="text-[9px] font-mono-ui tracking-[0.3em] uppercase">Scroll</span>
        <ChevronDown size={16} className="animate-bounce" />
      </button>
    </section>
  );
}
